import React, { useState, Suspense, useRef, useEffect } from 'react';
import AppLoading from '../app-loading/app-loading';
import { useTranslation } from 'react-i18next';
import { Toast } from "primereact/toast";
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import BinDetails from './bin-details';
import orderServices from '../../services/orders/orderservice';

interface City {
    subDistrictID: number;
    subDistrictName: string;
}

interface BinOrderData {
    bins: any[];
    totalRecordCount: number;
}

const Binorders: React.FC = () => {
    const { t } = useTranslation();
    const toast = useRef<Toast>(null);
    const [packingStatus, setPackingStatus] = useState<string[]>(['Unpacked']);
    const [cities, setCities] = useState<City[]>([]);
    const [selectedCities, setSelectedCities] = useState<City[]>([]);
    const [loading, setLoading] = useState(false);
    const [filteredData, setFilteredData] = useState<BinOrderData | null>(null);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const [startDate, setStartDate] = useState<any>(yesterday);
    const [endDate, setEndDate] = useState<any>(today);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    };


    const fetchCities = async () => {
        try {
            const citiesData = await orderServices.getOrderCities({
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
            });
            if (citiesData && citiesData.cities.length > 0) {
                setCities(citiesData.cities);
            } else {
                showToast('warn', 'Warning', 'No cities data available');
                setCities([]);
            }
        } catch (error) {
            console.error('Error fetching cities:', error);
            showToast('error', 'Error', 'Failed to load cities');
            setCities([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCities();
    }, [startDate, endDate]);

    const fetchFilteredData = async () => {
        try {
            setLoading(true);
            const SubdistrictIds = selectedCities.map(city => city.subDistrictID);
            if (packingStatus.length === 0) {
                showToast('warn', 'Warning', 'Please select at least one packing status');
                return;
            }

            const sortedStatus = [...packingStatus].sort((a, b) => {
                if (a === 'Unpacked') return -1;
                if (b === 'Unpacked') return 1;
                return 0;
            });

            const statusString = sortedStatus.join(',');
            const params = {
                packingStatus: statusString,
                startDate: formatDate(startDate),
                endDate: formatDate(endDate),
                SubdistrictIds: SubdistrictIds,
                pageNumber: pageNumber,
                pageSize: pageSize
            };
            const response = await orderServices.binOrderDetails(params);
            if (response) {
                setFilteredData(response);
            } else {
                showToast('warn', 'Warning', 'No data available for the selected filters');
                setFilteredData(null);
            }
        } catch (error) {
            console.error('Error filtering data:', error);
            showToast('error', 'Error', 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isInitialLoad) {
            fetchFilteredData();
            setIsInitialLoad(false);
        }
    }, [isInitialLoad]);

    useEffect(() => {
        if (!isInitialLoad) {
            setPageNumber(1);
            setPageSize(10);
            fetchFilteredData();
        }
    }, [selectedCities]);

    useEffect(() => {
        if (!isInitialLoad) {
            setPageNumber(1);
            setPageSize(10);
            fetchFilteredData();
        }
    }, [packingStatus]);

    useEffect(() => {
        if (!isInitialLoad) {
            fetchFilteredData();
        }
    }, [pageNumber, pageSize]);

    const handleStatusChange = (status: 'Packed' | 'Unpacked') => {
        setPackingStatus(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        );
    };

    const handleCityChange = (e: { value: City[] }) => {
        setSelectedCities(e.value);
    };

    const handleFilter = async () => {
        await fetchFilteredData();
    };

    const handlePageChange = (event: any) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        const newPageSize = event.rows;
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    };

    const showToast = (severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
        toast.current?.show({ severity, summary, detail, life: 3000 });
    };

    return (
        <Suspense fallback={<AppLoading />}>
            <Toast ref={toast} />
            <div className="layout-content mt-3 mb-3 grid grid-table-mobile">
                <h5 className="ml-1 mb-0 pb-3">{t("View Bin No Details")}</h5>
                <div className="col-12">
                    <Card>
                        <div className="flex flex-wrap align-items-center gap-3 mb-4">
                            <div className="flex align-items-center gap-3">
                                <Calendar
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="Start date"
                                    className="w-full"
                                />
                                <Calendar
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.value)}
                                    dateFormat="dd/mm/yy"
                                    placeholder="End date"
                                    className="w-full"
                                />
                                <Button
                                    label="Apply Filters"
                                    icon="pi pi-filter"
                                    className="p-button-primary"
                                    onClick={handleFilter}
                                    loading={loading}
                                    style={{ minWidth: '150px', height: '42px' }}
                                />
                            </div>
                        </div>

                        <div className="flex flex-wrap align-items-center gap-3 mb-4">
                            <div className="flex align-items-center gap-3">
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId="unpacked"
                                        checked={packingStatus.includes('Unpacked')}
                                        onChange={() => handleStatusChange('Unpacked')}
                                    />
                                    <label htmlFor="unpacked" className="ml-2">Unpacked</label>
                                </div>
                                <div className="flex align-items-center">
                                    <Checkbox
                                        inputId="packed"
                                        checked={packingStatus.includes('Packed')}
                                        onChange={() => handleStatusChange('Packed')}
                                    />
                                    <label htmlFor="packed" className="ml-2">Packed</label>
                                </div>
                            </div>

                            <div className="flex flex-column" style={{ minWidth: '200px' }}>
                                <div className="flex flex-column" style={{ minWidth: '200px' }}>
                                    <MultiSelect
                                        id="cities"
                                        value={selectedCities}
                                        options={cities}
                                        onChange={handleCityChange}
                                        optionLabel="subDistrictName"
                                        placeholder="Select Cities"
                                        display="chip"
                                        className="w-full"
                                        filter
                                        filterBy="subDistrictName"
                                        filterPlaceholder="Search cities"
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>

                <div className="col-12">
                    {loading ? (
                        <AppLoading />
                    ) : (
                        <BinDetails
                            data={filteredData}
                            onPageChange={handlePageChange}
                            pageNumber={pageNumber}
                            pageSize={pageSize}
                        />
                    )}
                </div>
            </div>
        </Suspense>
    );
};

export default Binorders;
